/** Hand-drawn marker-style doodles for SOL Kids.
 *  Inspired by children's colouring-book element aesthetic — flat marker fill,
 *  organic outlines, deliberately imperfect strokes.
 */

const W = (color = "#2C2C2A") => ({ stroke: color, strokeWidth: 2.2, strokeLinecap: "round", strokeLinejoin: "round", fill: "none" });

export const SunDoodle = ({ className = "", size = 80, color = "#F4C734" }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 100 100" fill="none" aria-hidden>
    <circle cx="50" cy="50" r="18" fill={color} />
    <g stroke={color} strokeWidth="3.6" strokeLinecap="round">
      <path d="M50 14 Q52 8 50 4" />
      <path d="M50 96 Q48 90 50 86" />
      <path d="M14 50 Q8 52 4 50" />
      <path d="M96 50 Q90 48 86 50" />
      <path d="M22 22 Q18 18 16 16" />
      <path d="M78 78 Q82 82 84 84" />
      <path d="M22 78 Q18 82 16 84" />
      <path d="M78 22 Q82 18 84 16" />
    </g>
  </svg>
);

export const Cloud = ({ className = "", size = 100, color = "#A9CDE5" }) => (
  <svg className={className} width={size} height={(size * 60) / 100} viewBox="0 0 100 60" fill="none" aria-hidden>
    <path d="M20 45 Q5 45 8 30 Q5 18 22 20 Q25 6 42 12 Q55 0 65 14 Q86 8 86 28 Q98 32 90 48 Q70 56 50 50 Q30 56 20 45 Z" fill={color} opacity="0.7"/>
  </svg>
);

export const Squiggle = ({ className = "", w = 120, color = "#2C2C2A" }) => (
  <svg className={className} width={w} height={w * 0.22} viewBox="0 0 120 26" fill="none" aria-hidden>
    <path d="M2 14 Q12 0 22 14 T42 14 T62 14 T82 14 T102 14 T118 14" {...W(color)}/>
  </svg>
);

export const Star = ({ className = "", size = 28, color = "#EE8C72", outline }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" aria-hidden>
    <path d="M12 2 L14.39 8.26 L21 8.27 L15.8 12.14 L17.78 18.73 L12 14.77 L6.22 18.73 L8.2 12.14 L3 8.27 L9.61 8.26 Z" fill={outline ? "none" : color} stroke={outline || color} strokeWidth={outline ? 2 : 0} strokeLinejoin="round"/>
  </svg>
);

export const PaperPlane = ({ className = "", size = 40, color = "#2C2C2A" }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 64 64" aria-hidden>
    <path d="M4 32 L60 8 L48 56 L34 38 L4 32 Z" {...W(color)}/>
    <path d="M34 38 L60 8" stroke={color} strokeWidth="2.2" strokeLinecap="round"/>
  </svg>
);

export const Rainbow = ({ className = "", w = 120 }) => (
  <svg className={className} width={w} height={w * 0.55} viewBox="0 0 120 66" fill="none" aria-hidden>
    <path d="M6 60 Q60 -6 114 60" stroke="#EE8C72" strokeWidth="6" strokeLinecap="round" fill="none"/>
    <path d="M16 60 Q60 6 104 60" stroke="#F4C734" strokeWidth="6" strokeLinecap="round" fill="none"/>
    <path d="M26 60 Q60 18 94 60" stroke="#8BC547" strokeWidth="6" strokeLinecap="round" fill="none"/>
    <path d="M36 60 Q60 30 84 60" stroke="#A9CDE5" strokeWidth="6" strokeLinecap="round" fill="none"/>
  </svg>
);

export const SunglassMark = ({ className = "", size = 40, color = "#2C2C2A" }) => (
  <svg className={className} width={size} height={(size * 22) / 64} viewBox="0 0 64 22" fill="none" aria-hidden>
    <circle cx="14" cy="11" r="9" {...W(color)}/>
    <circle cx="50" cy="11" r="9" {...W(color)}/>
    <path d="M23 9 Q32 6 41 9" {...W(color)}/>
    <path d="M5 7 L1 5" {...W(color)}/>
    <path d="M59 7 L63 5" {...W(color)}/>
  </svg>
);

/** Marker flower with a smile. */
export const Flower = ({ className = "", size = 64, color = "#EE8C72", center = "#F4C734" }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 64 64" aria-hidden>
    <g fill={color}>
      <ellipse cx="32" cy="14" rx="9" ry="11"/>
      <ellipse cx="32" cy="50" rx="9" ry="11"/>
      <ellipse cx="14" cy="32" rx="11" ry="9"/>
      <ellipse cx="50" cy="32" rx="11" ry="9"/>
    </g>
    <circle cx="32" cy="32" r="10" fill={center}/>
    <circle cx="29" cy="31" r="1.4" fill="#2C2C2A"/>
    <circle cx="35" cy="31" r="1.4" fill="#2C2C2A"/>
    <path d="M28 35 Q32 38 36 35" stroke="#2C2C2A" strokeWidth="1.6" fill="none" strokeLinecap="round"/>
  </svg>
);

/** Butterfly silhouette. */
export const Butterfly = ({ className = "", size = 60, color = "#C6B9D6" }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 64 64" aria-hidden>
    <g fill={color}>
      <path d="M32 32 C 20 16, 6 18, 4 30 C 6 42, 22 46, 32 32 Z"/>
      <path d="M32 32 C 44 16, 58 18, 60 30 C 58 42, 42 46, 32 32 Z"/>
      <path d="M32 32 C 24 38, 18 48, 22 56 C 28 56, 30 46, 32 36 Z"/>
      <path d="M32 32 C 40 38, 46 48, 42 56 C 36 56, 34 46, 32 36 Z"/>
    </g>
    <path d="M32 14 L 32 36" stroke="#2C2C2A" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="29" cy="13" r="2" fill="#2C2C2A"/>
    <circle cx="35" cy="13" r="2" fill="#2C2C2A"/>
  </svg>
);

/** Music note pair. */
export const MusicNote = ({ className = "", size = 48, color = "#3D5C99" }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 64 64" aria-hidden>
    <path d="M22 48 V18 L48 12 V42" stroke={color} strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
    <ellipse cx="18" cy="48" rx="6" ry="4.5" fill={color}/>
    <ellipse cx="44" cy="42" rx="6" ry="4.5" fill={color}/>
    <path d="M22 18 L48 12" stroke={color} strokeWidth="3" strokeLinecap="round"/>
  </svg>
);

/** Scribbled crayon mark. */
export const Scribble = ({ className = "", w = 80, color = "#F4C734" }) => (
  <svg className={className} width={w} height={w * 0.45} viewBox="0 0 80 36" aria-hidden>
    <path d="M4 26 C 14 14, 22 30, 30 16 S 50 30, 58 14 S 74 28, 78 12"
      stroke={color} strokeWidth="6" strokeLinecap="round" fill="none"/>
  </svg>
);

/** Cute frog face (no Peppa references — just a generic marker frog). */
export const Frog = ({ className = "", size = 60, color = "#8BC547" }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 64 64" aria-hidden>
    <ellipse cx="32" cy="38" rx="22" ry="18" fill={color}/>
    <circle cx="20" cy="20" r="9" fill={color}/>
    <circle cx="44" cy="20" r="9" fill={color}/>
    <circle cx="20" cy="20" r="4" fill="#FAFAF7"/>
    <circle cx="44" cy="20" r="4" fill="#FAFAF7"/>
    <circle cx="20" cy="21" r="2.2" fill="#2C2C2A"/>
    <circle cx="44" cy="21" r="2.2" fill="#2C2C2A"/>
    <path d="M20 40 Q32 50 44 40" stroke="#2C2C2A" strokeWidth="2.2" fill="none" strokeLinecap="round"/>
    <circle cx="26" cy="36" r="1.5" fill="#2C2C2A"/>
    <circle cx="38" cy="36" r="1.5" fill="#2C2C2A"/>
  </svg>
);

/** Ladybug-ish dot bug (without character branding). */
export const Bug = ({ className = "", size = 50, color = "#DD2E20" }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 64 64" aria-hidden>
    <ellipse cx="32" cy="34" rx="22" ry="18" fill={color}/>
    <path d="M32 16 V52" stroke="#2C2C2A" strokeWidth="2.2"/>
    <circle cx="22" cy="28" r="2.5" fill="#2C2C2A"/>
    <circle cx="42" cy="28" r="2.5" fill="#2C2C2A"/>
    <circle cx="22" cy="42" r="2.5" fill="#2C2C2A"/>
    <circle cx="42" cy="42" r="2.5" fill="#2C2C2A"/>
    <circle cx="26" cy="14" r="4" fill="#2C2C2A"/>
    <circle cx="38" cy="14" r="4" fill="#2C2C2A"/>
  </svg>
);

/** Hand-drawn bird. */
export const Bird = ({ className = "", size = 56, color = "#3D5C99" }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 64 64" aria-hidden>
    <path d="M14 38 Q10 24 28 22 Q44 22 48 32 Q58 32 58 42 L 22 44 Q14 44 14 38 Z" fill={color}/>
    <path d="M48 32 L 56 28" stroke={color} strokeWidth="3" strokeLinecap="round"/>
    <path d="M48 32 L 60 30" stroke="#F4C734" strokeWidth="3" strokeLinecap="round"/>
    <circle cx="46" cy="30" r="1.5" fill="#FAFAF7"/>
  </svg>
);

/** Crayon. */
export const Crayon = ({ className = "", w = 80, color = "#F4C734" }) => (
  <svg className={className} width={w} height={w * 0.3} viewBox="0 0 80 24" aria-hidden>
    <rect x="14" y="6" width="50" height="12" fill={color} stroke="#2C2C2A" strokeWidth="1.5"/>
    <path d="M14 6 L 4 12 L 14 18 Z" fill={color} stroke="#2C2C2A" strokeWidth="1.5" strokeLinejoin="round"/>
    <rect x="22" y="6" width="2" height="12" fill="#2C2C2A" opacity="0.25"/>
    <rect x="54" y="6" width="2" height="12" fill="#2C2C2A" opacity="0.25"/>
    <rect x="58" y="6" width="6" height="12" fill="#FAFAF7" opacity="0.4"/>
  </svg>
);

/** Loose hand drawn arrow. */
export const HandArrow = ({ className = "", w = 80, color = "#2C2C2A" }) => (
  <svg className={className} width={w} height={w * 0.5} viewBox="0 0 80 40" aria-hidden>
    <path d="M4 30 Q 24 4, 70 18" {...W(color)}/>
    <path d="M62 12 L 72 18 L 64 28" {...W(color)}/>
  </svg>
);

/** Soft pastel blob (for asymmetric background dots) */
export const Blob = ({ className = "", size = 200, color = "#F4C734" }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 200 200" aria-hidden>
    <path d="M40 90 C 20 50, 70 10, 110 18 C 160 26, 190 70, 178 110 C 168 160, 110 188, 70 168 C 30 150, 60 130, 40 90 Z" fill={color}/>
  </svg>
);

/** Tiny heart */
export const Heart = ({ className = "", size = 24, color = "#DD2E20" }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" aria-hidden>
    <path d="M12 21 C 4 14 2 9 6 6 C 9 4 11 6 12 8 C 13 6 15 4 18 6 C 22 9 20 14 12 21 Z" fill={color}/>
  </svg>
);

export const WhatsAppIcon = ({ size = 18, color = "currentColor", className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden className={className}>
    <path d="M19.05 4.91A9.82 9.82 0 0012.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.9 9.9 0 004.74 1.21h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.91-7.01zM12.05 20.15h-.01a8.23 8.23 0 01-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.24 8.24 0 01-1.26-4.38c0-4.55 3.7-8.25 8.25-8.25 2.2 0 4.27.86 5.83 2.42a8.18 8.18 0 012.42 5.83c0 4.55-3.7 8.25-8.24 8.25zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.12-.16.25-.64.81-.78.97-.14.16-.29.18-.54.06-.25-.12-1.05-.39-2-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.02-.39.11-.51.11-.11.25-.29.37-.43.12-.14.16-.25.25-.41.08-.16.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.4-.42-.56-.42-.14 0-.31-.02-.48-.02-.16 0-.43.06-.66.31-.23.25-.86.84-.86 2.05 0 1.21.88 2.37 1 2.53.12.16 1.73 2.65 4.2 3.71.59.25 1.04.41 1.4.52.59.19 1.12.16 1.55.1.47-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.15-1.18-.06-.11-.23-.18-.48-.31z"/>
  </svg>
);
